import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router';

import {
  fetchExpertShowcaseSection,
  removeExpertShowcaseSection,
  updateExpertShowcaseSection,
} from '@/actions/expertShowcase';
import { notify } from '@/actions/ui';
import { SelectProfile } from '@/components/FormAdapters';

import ModelForm from '../ModelForm';

function ExpertShowcaseSectionModel({
  id,
  model,
  fetchExpertShowcaseSection,
  updateExpertShowcaseSection,
  notify,
  section,
}: any) {
  const navigate = useNavigate();
  useEffect(() => {
    if (id) {
      fetchExpertShowcaseSection(id).then((page: any) => {
        if (!page) {
          notify('Not found', 'warning');
          navigate(`/admin/expert_showcases`);
        }
      });
    }
  }, [id]);

  const validateProfiles = useCallback((profiles: any) => {
    const profileIds = profiles?.map((p: any) => p.profile_id);
    return profiles?.map((p: any) => {
      const errors = {
        profile_id: required(p.profile_id),
        order: required(p.order),
      };
      if (profileIds.indexOf(p.profile_id) !== profileIds.lastIndexOf(p.profile_id)) {
        errors.profile_id = `Profile is duplicated`;
      }
      return errors;
    });
  }, []);

  const validateCtaLabel = useCallback((ctaLabel: any, values: any) => {
    if (!ctaLabel && values.cta_url) {
      return 'Required (CTA URL is informed)';
    }

    if (ctaLabel && !ctaLabel.trim()) {
      return 'Invalid value';
    }

    return undefined;
  }, []);

  const validateCtaUrl = useCallback((ctaUrl: any, values: any) => {
    if (!ctaUrl && values.cta_label) {
      return 'Required (CTA Label is informed)';
    }

    if (ctaUrl && !ctaUrl.trim()) {
      return 'Invalid value';
    }

    return undefined;
  }, []);

  const editableFields = useMemo(
    () => [
      { name: 'icon', type: 'text', required: true },
      { name: 'name', type: 'text', required: true },
      { name: 'slug', type: 'text', required: true },
      { name: 'order', type: 'number', required: true },
      {
        name: 'cta_label',
        label: 'CTA Label',
        type: 'text',
        maxLength: 40,
        validate: validateCtaLabel,
      },
      {
        name: 'cta_url',
        label: 'CTA URL',
        type: 'text',
        maxLength: 256,
        validate: validateCtaUrl,
      },
      {
        name: 'profiles',
        validate: validateProfiles,
        fields: [
          {
            name: 'profile_id',
            component: (props: any) => {
              const { value } = props.input;
              // TODO: Figure out how to get profile info other than ID
              const profile = section.profiles_raw?.find((p: any) => p.profile_id === value);
              return (
                <SelectProfile
                  {...props}
                  value={
                    profile
                      ? {
                          type: 'profile',
                          value: {
                            id: value,
                            name: profile.profile_name,
                          },
                        }
                      : null
                  }
                  label="Profile"
                  trapEnter
                />
              );
            },
          },
          { name: 'name', label: 'Name (override)', type: 'text' },
          { name: 'picture', label: 'Picture (override)', type: 'file' },
          { name: 'city', label: 'City (override)', type: 'text' },
          { name: 'country', label: 'Country (override)', type: 'text' },
          { name: 'experience', label: 'Experience (override)', type: 'text' },
          { name: 'title', label: 'Headline (override)', type: 'text' },
          {
            name: 'summary',
            label: 'Short Bio (override)',
            type: 'textarea',
            maxLength: 2000,
          },
          {
            name: 'marketing_keywords',
            type: 'chips',
            maxLength: 40,
            maxChips: 30,
          },
          { name: 'order', type: 'number' },
          { name: 'highlight', type: 'checkbox' },
          { name: 'badge_text', type: 'text', maxLength: 40 },
        ],
      },
    ],
    [section]
  );

  const handleSubmit = useCallback(async (values: any) => {
    values.profiles.forEach((p: any) => {
      delete p.profile_name;
      delete p.file_picture;
    });
    const section = await updateExpertShowcaseSection(values);
    navigate(`/admin/${model.path}/${section.id}`);
  }, []);

  const handleDelete = useCallback(() => removeExpertShowcaseSection(id), []);

  const required = useCallback((value: any) => (value ? undefined : 'Required'), []);

  return (
    <ModelForm
      title={`Section ${id} - ${section.name}`}
      id={id}
      model={model}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
      initialModel={{
        ...section,
        profiles: section.profiles_raw,
      }}
      fields={editableFields}
      returnPath={`expert_showcase_categories/${section.category_id}`}
    />
  );
}

// @ts-expect-error TS(2630) FIXME: Cannot assign to 'ExpertShowcaseSectionModel' beca... Remove this comment to see the full error message
ExpertShowcaseSectionModel = connect(
  (state, ownProps) => {
    return {
      // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
      section: state.expertShowcase.sections[ownProps.id] || {},
    };
  },
  {
    fetchExpertShowcaseSection,
    updateExpertShowcaseSection,
    removeExpertShowcaseSection,
    notify,
  }
)(ExpertShowcaseSectionModel);

export default ExpertShowcaseSectionModel;
